import { useState } from 'react';
import { useAtom } from 'jotai';
import { profileAtom } from '../../../Settings/atom';
import { useNavigate } from 'react-router-dom';

import FireIcon from '../../../../assets/fire-icon.svg';
import ArrowUpBlueIcon from '../../../../assets/arrow-up-blue-icon.svg';
import DiamondShineIcon from '../../../../assets/diamond-shine.svg';
import RisingUpIcon from '../../../../assets/rising-up.svg';
import StarHalfIcon from '../../../../assets/star-half.svg';
import TrendingDownIcon from '../../../../assets/trending-down.svg';
import CognitiveIcon from '../../../../assets/cognitive-icon.svg';
import WriteIcon from '../../../../assets/write-icon.svg';
import WriteGreenIcon from '../../../../assets/write-green-icon.svg';
import TwygsLogoIcon from '../../../../assets/twyg-logo-icon.svg';
import BalanceIcon from '../../../../assets/balance-icon.svg';
import EmojiPeopleIcon from '../../../../assets/emoji-people.svg';
import CampaignIcon from '../../../../assets/campaign-icon.svg';
import ModelTrainingIcon from '../../../../assets/model-training-icon.svg';

const ExternalNewsCard = ({ item, hideWriteButton }) => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [profile] = useAtom(profileAtom);

  const getPopularityStatus = (urgencyIndicator) => {
    switch (urgencyIndicator) {
      case 'PEAKING':
        return {
          text: 'Hot right now',
          className:
            'bg-gradient-to-b from-[#FF7D4E] via-[#FDA704] to-[#FFC15C] bg-clip-text text-transparent',
          icon: FireIcon,
          iconSize: 'w-6 h-6',
          description: 'High visibility!'
        };
      case 'RISING':
        return {
          text: 'Emerging topic',
          className: 'text-[#4282A9]',
          icon: ArrowUpBlueIcon,
          iconSize: 'w-[12px] h-[13px]',
          description: 'Moderate popularity, growing interest'
        };
      case 'STABLE':
        return {
          text: 'Hidden gem',
          className: 'text-[#8A38F5]',
          icon: DiamondShineIcon,
          iconSize: 'w-6 h-6',
          description: 'Low visibility, strong writing potential'
        };
      default:
        return { text: 'Trending', className: 'text-green-600', icon: '📈' };
    }
  };

  const getCoverageStatus = (readerDemand) => {
    switch (readerDemand) {
      case 'HIGH':
        return {
          text: 'High',
          className: 'text-growth-green-500',
          icon: RisingUpIcon,
          iconSize: 'w-5 h-5',
          description: 'Lots written, challenge views or dig deeper!'
        };
      case 'MODERATE':
        return {
          text: 'Medium',
          className: 'text-[#C39E53]',
          icon: StarHalfIcon,
          iconSize: 'w-5 h-5',
          description: 'Some coverage, room for fresh perspectives!'
        };
      default:
        return {
          text: 'Low',
          className: 'text-writer-earth-brown-500',
          icon: TrendingDownIcon,
          iconSize: 'w-5 h-5',
          description: 'Be first, share your fresh perspective!'
        };
    }
  };

  const handleRelatedStoriesClick = () => {
    // Navigate to related articles page for this story
    navigate(`/related-articles?storyId=${encodeURIComponent(item.id)}`);
  };

  const handleSourceClick = () => {
    // Open the source URL in a new tab
    if (item.sourceUrl && item.sourceUrl !== '#') {
      window.open(item.sourceUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const handleWriteAboutThis = () => {
    // Navigate to article creation with pre-filled topic
    navigate(`/article/create?storyId=${encodeURIComponent(item.id)}`);
  };

  // Process viewpoints to ensure consistent structure
  const processedViewpoints = (item.viewpoints || []).map((viewpoint, index) => {
    // Handle both old structure (perspective/summary) and new structure (content/title)
    if (typeof viewpoint === 'string') {
      return {
        perspective: `Perspective ${index + 1}`,
        summary: viewpoint
      };
    } else if (viewpoint.content || viewpoint.title) {
      return {
        perspective: viewpoint.title || `Viewpoint ${index + 1}`,
        summary: viewpoint.content || viewpoint.summary || 'No summary available'
      };
    } else {
      return {
        perspective: viewpoint.stakeholder || viewpoint.perspective || `Perspective ${index + 1}`,
        summary: viewpoint.viewpoint || viewpoint.summary || 'No summary available'
      };
    }
  });

  function getPopularityComponent(urgencyIndicator) {
    const popularityStatus = getPopularityStatus(urgencyIndicator);

    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-[black] font-bold">Popularity:</span>
        <span className={`${popularityStatus.className} font-[900] whitespace-nowrap`}>
          {popularityStatus.text}
        </span>
        <img className={`${popularityStatus.iconSize}`} src={popularityStatus.icon}></img>
        <span className="text-sm font-medium">{popularityStatus.description}</span>
      </div>
    );
  }

  function getCoverageComponent(readerDemand) {
    const coverageStatus = getCoverageStatus(readerDemand);

    return (
      <div className="flex items-center gap-2">
        <span className="text-sm text-[black] font-bold">Coverage:</span>
        <span className={`${coverageStatus.className} font-[900] whitespace-nowrap`}>
          {coverageStatus.text}
        </span>
        <img className={`${coverageStatus.iconSize}`} src={coverageStatus.icon}></img>
        <span className="text-sm">{coverageStatus.description}</span>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-200">
      {/* Top section with category, time, and related articles */}
      <div className="flex items-center gap-2 mb-4">
        <span
          className={`bg-growth-green-50 px-3 py-1 rounded-full text-sm font-extrabold text-growth-green-900`}
        >
          {item.category}
        </span>
        <span className="text-gray-400">•</span>
        <span className="text-sm text-gray-600">Posted {item.timeAgo}</span>
        <span className="text-gray-400">•</span>
        <div
          className="flex items-center gap-1 text-green-600 cursor-pointer"
          onClick={handleRelatedStoriesClick}
        >
          <img className="w-4 h-4" src={TwygsLogoIcon}></img>
          <span className="text-sm font-medium">
            {item.numberRelatedArticle || 0} related articles
          </span>
        </div>
      </div>

      {/* Popularity and Coverage indicators */}
      <div className="mb-4 space-y-2">
        {getPopularityComponent(item.urgencyIndicator)}
        {getCoverageComponent(item.readerDemand)}
      </div>

      {/* Title and description */}
      <div className="mb-4">
        <h3 className="text-lg font-extrabold text-gray-900 mb-3 leading-tight">{item.title}</h3>
        <p className="text-sm text-gray-700 font-normal leading-relaxed">{item.description}</p>
      </div>

      {/* Learn More link */}
      <div className="mb-4">
        <button
          onClick={handleSourceClick}
          className="text-growth-green-500 hover:text-growth-green-600 font-[900] text-sm flex items-center gap-1 transition-colors"
        >
          Learn More
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={3}
              d="M7 17l9.2-9.2M17 17V7H7"
            />
          </svg>
        </button>
      </div>

      {/* Bottom section with perspectives and actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1 text-growth-green-500">
            <img className="w-5 h-5" src={CognitiveIcon}></img>
            <span className="text-sm font-[900]">
              {processedViewpoints.length || 0} Perspectives
            </span>
          </div>

          {!hideWriteButton && profile?.isCreator && (
            <button
              onClick={handleWriteAboutThis}
              className="bg-growth-green-500 hover:bg-growth-green-600 text-white font-[900] text-sm px-4 py-2 rounded-lg transition-colors flex items-center gap-2 shadow-md"
            >
              Write About this
              <img className="w-5 h-5" src={WriteIcon}></img>
            </button>
          )}
        </div>

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-gray-400 hover:text-gray-600 transition-colors p-1"
        >
          <svg
            className={`w-5 h-5 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
          {/* Viewpoints Section */}
          {processedViewpoints && processedViewpoints.length > 0 && (
            <div className="rounded-lg p-4 border">
              <div>
                <div className="flex items-center gap-1 text-growth-green-500">
                  <span className="text-sm font-[900]">Perspectives</span>
                  <img className="w-5 h-5" src={CognitiveIcon}></img>
                </div>
                <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  Discover what people are saying.
                </h4>
              </div>
              <div className="space-y-3">
                {processedViewpoints.map((viewpoint, index) => (
                  <div key={index} className="rounded-sm p-4 border">
                    <div className="flex-1 min-w-0">
                      <h5 className="text-sm font-bold text-green-900 mb-2">
                        {viewpoint.perspective}
                      </h5>
                      <p className="text-sm text-green-800 leading-relaxed">{viewpoint.summary}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Writing inspiration based on viewpoints */}
          <div className="mt-4 rounded-lg p-[2px] bg-gradient-to-r from-[#A6D38D] via-[#5A8A3A] to-[#205323]">
            <div className="bg-white rounded-lg p-4">
              <div className="mb-2">
                <h5 className="text-sm text-growth-green-500 flex items-center gap-1 font-[900]">
                  Writing Opportunities
                  <img className="w-5 h-5" src={WriteGreenIcon}></img>
                </h5>
                <p className="text-[13px]">Ideas to spike your creativity.</p>
              </div>
              <div className="text-xs text-green-700 space-y-2">
                <div className="flex items-center gap-2">
                  <img className="w-5 h-5" src={EmojiPeopleIcon}></img>
                  <span>Challenge or support one of these perspectives with new evidence</span>
                </div>
                <div className="flex items-center gap-2">
                  <img className="w-4 h-4" src={BalanceIcon}></img>
                  <span>Find the middle ground between opposing viewpoints</span>
                </div>
                <div className="flex items-center gap-2">
                  <img className="w-5 h-5" src={CampaignIcon}></img>
                  <span>Offer a completely fresh angle not yet discussed</span>
                </div>
                <div className="flex items-center gap-2">
                  <img className="w-5 h-5" src={ModelTrainingIcon}></img>
                  <span>Deep-dive into the implications of one perspective</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tags and categories */}
          {item.tags && item.tags.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">Related topics:</p>
              <div className="flex flex-wrap gap-2">
                {item.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-gray-100 rounded-full text-xs text-gray-700 hover:bg-gray-200 cursor-pointer transition-colors"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExternalNewsCard;
